//The list of all the player textures, both the default models and the partial textures used for customization

import type { WokaList, WokaPartType } from "@workadventure/messages";

export interface WokaTextureDescriptionListInterface {
    [key: string]: WokaTextureDescriptionInterface;
}

export interface WokaTextureDescriptionInterface {
    id: string;
    url: string;
}

export enum PlayerTexturesKey {
    Accessory = "accessory",
    Body = "body",
    Bottom = "bottom",
    Clothes = "clothes",
    Eyes = "eyes",
    Hair = "hair",
    Hat = "hat",
    Jacket = "jacket",
    Shoes = "shoes",
    Woka = "woka",
}

/**
 * INSECAP: orden de dibujo de las capas, de atrás hacia adelante.
 *
 * `clothes` es la prenda de arriba y `bottom` la de abajo, así que la de abajo
 * va primero. Los zapatos van después de la ropa —no antes— porque los
 * conjuntos de una pieza traen su propio pantalón y el zapato tiene que
 * quedar encima. La chaqueta va sobre la prenda de arriba, y pelo, gorro y
 * accesorio cierran.
 */
const ORDEN_DE_CAPAS: PlayerTexturesKey[] = [
    PlayerTexturesKey.Body,
    PlayerTexturesKey.Eyes,
    PlayerTexturesKey.Bottom,
    PlayerTexturesKey.Clothes,
    PlayerTexturesKey.Jacket,
    PlayerTexturesKey.Shoes,
    PlayerTexturesKey.Hair,
    PlayerTexturesKey.Hat,
    PlayerTexturesKey.Accessory,
];

export class PlayerTextures {
    // una entrada por capa, indexada por su nombre: agregar una capa nueva es
    // agregarla a wokaPartNames y a ORDEN_DE_CAPAS, sin más campos sueltos
    private recursos = new Map<string, WokaTextureDescriptionListInterface>();
    private layers: WokaTextureDescriptionListInterface[] = [];

    wokaCollections = new Map<string, WokaTextureDescriptionInterface[]>();

    public loadPlayerTexturesMetadata(metadata: WokaList): void {
        this.mapTexturesMetadataIntoResources(metadata);
    }

    public getTexturesResources(key: PlayerTexturesKey): WokaTextureDescriptionListInterface {
        return this.recursos.get(key) ?? {};
    }

    public getLayers(): WokaTextureDescriptionListInterface[] {
        return this.layers;
    }

    public getCollectionsKeys(): string[] {
        return Array.from(this.wokaCollections.keys());
    }

    public getWokaCollectionTextures(key: string): WokaTextureDescriptionInterface[] {
        return this.wokaCollections.get(key) ?? [];
    }

    private mapTexturesMetadataIntoResources(metadata: WokaList): void {
        this.recursos.clear();
        for (const key of Object.values(PlayerTexturesKey)) {
            this.recursos.set(key, this.getMappedResources(metadata[key]));
        }

        // Una capa que el woka.json no traiga queda como objeto vacío: el motor
        // la ignora y el personaje se dibuja igual. Es lo que permite publicar
        // las capas nuevas sin romper a quien tenga un woka guardado de antes.
        this.layers = ORDEN_DE_CAPAS.map((key) => this.recursos.get(key) ?? {});

        this.mapWokaCollections(metadata.woka);
    }

    private getMappedResources(category: WokaPartType): WokaTextureDescriptionListInterface {
        const resources: WokaTextureDescriptionListInterface = {};
        if (!category) {
            return {};
        }
        for (const collection of category.collections) {
            for (const texture of collection.textures) {
                resources[texture.id] = { id: texture.id, url: texture.url };
            }
        }
        return resources;
    }

    private mapWokaCollections(category: WokaPartType): void {
        if (!category) {
            return;
        }
        for (const collection of category.collections) {
            const textures: WokaTextureDescriptionInterface[] = [];
            for (const texture of collection.textures) {
                textures.push({ id: texture.id, url: texture.url });
            }
            this.wokaCollections.set(collection.name, textures);
        }
    }
}
